import { financialStatementsEndpoints } from '../api/financialStatementsEndpoints'
import backendRequest from '../../../services/api/backendRequest'
import type {
  DimensionQueryParams,
  DimensionStetementsResponse,
  InvestmentReportRow,
  AmendmentReportRow
} from '../types'
import { DimensionQueryOperator } from '../../dimension/types/Dimension'
import { BudgetingScenario } from '../../scenario/types/BudgetingScenario'

/**
 * Standalone function to fetch dimension data
 * Can be used with or without react-query
 */
export const fetchDimensionData = async (
  params: DimensionQueryParams,
  signal?: AbortSignal
): Promise<DimensionStetementsResponse> => {
  const { companyId, accountCode, statementRowId, dimensionIds, budgetingScenarioId, periodGroups } = params

  const response = await backendRequest<DimensionStetementsResponse>({
    method: 'GET',
    url: financialStatementsEndpoints.getFinancialStatement(companyId),
    params: {
      statementRowId,
      accountCode,
      dimensions: JSON.stringify(
        dimensionIds?.map(id => ({
          k: id,
          o: DimensionQueryOperator.has,
          v: id
        }))
      ),
      budgetingScenarioId,
      periodGroups
    },
    signal
  })

  return response
}

/**
 * Standalone function to fetch investment report data
 * Can be used with or without react-query
 */
export const fetchInvestmentData = async (
  companyId: string,
  accountCode: number,
  budgetingScenarioId?: BudgetingScenario['id'],
  periodGroups?: DimensionQueryParams['periodGroups'],
  signal?: AbortSignal
): Promise<InvestmentReportRow[]> => {
  const response = await backendRequest<InvestmentReportRow[]>({
    method: 'GET',
    url: financialStatementsEndpoints.getInvestments(companyId),
    params: {
      accountCode,
      budgetingScenarioId,
      periodGroups
    },
    signal
  })

  return response
}

export interface AmendmentQueryParams {
  accountCode: number
  budgetingScenarioId?: BudgetingScenario['id']
  periodGroups?: DimensionQueryParams['periodGroups']
  signal?: AbortSignal
}

/**
 * Standalone function to fetch amendment report data
 * Can be used with or without react-query
 */
export const fetchAmendmentData = async (
  companyId: string,
  params: AmendmentQueryParams
): Promise<AmendmentReportRow[]> => {
  const { signal, ...requestParams } = params

  const response = await backendRequest<AmendmentReportRow[]>({
    method: 'GET',
    url: financialStatementsEndpoints.getAmendments(companyId),
    params: requestParams,
    signal
  })

  return response
}

/**
 * Standalone function to fetch counter amendment report data
 * Can be used with or without react-query
 */
export const fetchCounterAmendmentData = async (
  companyId: string,
  params: AmendmentQueryParams
): Promise<AmendmentReportRow[]> => {
  const { signal, ...requestParams } = params

  const response = await backendRequest<AmendmentReportRow[]>({
    method: 'GET',
    url: financialStatementsEndpoints.getCounterAmendments(companyId),
    params: requestParams,
    signal
  })

  return response
}
